import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, UtensilsCrossed } from 'lucide-react';
import {
  UtilizationProductRow,
  UnmappedProductRow,
  UtilizationTab,
  UtilizationFilters,
  UTILIZATION_TABS,
  UtilizationStatus,
  isUnmappedTab,
} from '../../../../../utils/reports/utilizationReport';
import { ColumnFilterDropdown } from '../../../../ui/ColumnFilterDropdown';
import { cn } from '../../../../../lib/utils';
import { FilterDropdown, FilterOption } from './FilterDropdown';

interface UtilizationControlsProps {
  /** Side-scoped mapped rows BEFORE the tab filter — the option lists for every tab
   *  except `unmapped`, which draws its supplier/category options from its own rows. */
  rows: UtilizationProductRow[];
  unmappedRows: UnmappedProductRow[];
  tab: UtilizationTab;
  onTabChange: (tab: UtilizationTab) => void;
  /** Per-tab row counts, computed by the page on the filtered rows. */
  counts: Record<UtilizationTab, number>;
  filters: UtilizationFilters;
  onFiltersChange: (filters: UtilizationFilters) => void;
}

const uniq = (xs: string[]) => Array.from(new Set(xs)).sort();

/** Small label + multi-select dropdown pair, matching the supplier report's
 *  standalone filter controls. */
const LabeledFilter: React.FC<{
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  options: string[] | { id: string; name: string }[];
  valueType?: 'default' | 'category';
}> = ({ label, selected, onChange, options, valueType = 'default' }) => (
  <div className="flex items-center gap-1.5 h-11 px-3 rounded-xl border border-border dark:border-border-dark">
    <span className="text-sm font-medium text-muted dark:text-muted-dark whitespace-nowrap">{label}</span>
    {selected.length > 0 && (
      <span className="min-w-[20px] h-5 px-1 rounded-full bg-[#0E8965]/15 dark:bg-[#0E8965]/15 text-[#0E8965] dark:text-[#0E8965] text-xs font-semibold flex items-center justify-center">
        {selected.length}
      </span>
    )}
    <ColumnFilterDropdown
      selectedValues={selected}
      onValuesChange={onChange}
      availableOptions={options}
      valueType={valueType}
      filterLabel={label}
      maxHeight="max-h-[240px]"
      align="left"
    />
  </div>
);

/**
 * The report's single scope control: the page tabs plus the shared filter set.
 * Whatever is selected here scopes the KPI cards, the over-ordering chart, the
 * Ordered-vs-Needed table and the waste donuts — the page always answers one
 * question at a time. (The "purchased but not linked" section below is a different
 * row type and keeps its own filters.)
 */
export const UtilizationControls: React.FC<UtilizationControlsProps> = ({
  rows,
  unmappedRows,
  tab,
  onTabChange,
  counts,
  filters,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  // Status and recipe have no counterpart on a spend-only unmapped row.
  const onUnmapped = isUnmappedTab(tab);

  // Options come from whichever row set the active tab is actually showing.
  const optionRows: Array<{ supplier: string; category?: string }> = onUnmapped ? unmappedRows : rows.flatMap(row => row.restaurantContributions ?? [row]);
  const supplierOptions = useMemo(() => uniq(optionRows.map((r) => r.supplier)), [optionRows]);
  const categoryOptions = useMemo(() => uniq(optionRows.map((r) => r.category).filter((c): c is string => !!c)), [optionRows]);
  const statusOptions = useMemo(
    () => (['critical', 'review', 'healthy'] as UtilizationStatus[]).map((s) => ({ id: s, name: t(`reports.utilization.status.${s}`) })),
    [t]
  );
  const recipeOptions: FilterOption[] = useMemo(
    () => [
      { value: 'all', label: t('reports.utilization.filter.allRecipes') },
      ...uniq(rows.flatMap((r) => r.dishes)).map((d) => ({ value: `dish:${d}`, label: d })),
      ...uniq(rows.flatMap((r) => r.subrecipes)).map((s) => ({ value: `sub:${s}`, label: s })),
    ],
    [rows, t]
  );

  const set = (patch: Partial<UtilizationFilters>) => onFiltersChange({ ...filters, ...patch });

  return (
    <div data-onboarding="utilization-controls utilization-tabs" className="bg-background dark:bg-background-dark rounded-xl shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] p-4 mb-6">
      <div className="flex items-center gap-3 border-b border-border dark:border-border-dark mb-3 overflow-x-auto">
        {UTILIZATION_TABS.map((id) => {
          const active = tab === id;
          return (
            <button
              key={id}
              data-onboarding={`utilization-tab-${id}`}
              aria-pressed={active}
              onClick={() => onTabChange(id)}
              className={cn(
                'flex items-center gap-2 px-3 py-3 border-b-2 transition-colors rounded-t-lg whitespace-nowrap flex-shrink-0',
                active
                  ? 'border-[#18B081] dark:border-[#18B081] text-[#0E8965] dark:text-[#0E8965] bg-[#0E8965]/5 dark:bg-[#0E8965]/5'
                  : 'border-transparent dark:border-transparent text-muted dark:text-muted-dark hover:text-textPrimary dark:hover:text-textPrimary-dark'
              )}
            >
              <span className="text-sm font-semibold">{t(`reports.utilization.tab.${id}`)}</span>
              <span
                className={cn(
                  'min-w-[24px] h-6 px-1 rounded-full border flex items-center justify-center text-xs font-medium',
                  active
                    ? 'bg-[#0E8965]/15 text-[#18B081] border-transparent'
                    : 'bg-backgroundDarker dark:bg-backgroundDarker-dark text-muted dark:text-muted-dark border-border dark:border-border-dark'
                )}
              >
                {counts[id]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col lg:flex-row gap-2">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted dark:text-muted-dark" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => set({ search: e.target.value })}
            placeholder={t('reports.utilization.filter.searchProduct')}
            className="w-full h-11 pl-9 pr-4 rounded-xl text-sm bg-background dark:bg-background-dark border border-border dark:border-border-dark text-textPrimary dark:text-textPrimary-dark placeholder:text-muted dark:placeholder:text-muted-dark focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {!onUnmapped && (
            <FilterDropdown
              label={t('reports.utilization.filter.dish')}
              icon={<UtensilsCrossed className="w-5 h-5" />}
              options={recipeOptions}
              value={filters.recipe}
              onChange={(recipe) => set({ recipe })}
              searchable
            />
          )}
          <LabeledFilter
            label={t('reports.utilization.col.category')}
            selected={filters.categories}
            onChange={(categories) => set({ categories })}
            options={categoryOptions}
            valueType="category"
          />
          <LabeledFilter
            label={t('reports.utilization.col.supplier')}
            selected={filters.suppliers}
            onChange={(suppliers) => set({ suppliers })}
            options={supplierOptions}
          />
          {!onUnmapped && (
            <LabeledFilter
              label={t('reports.utilization.col.status')}
              selected={filters.statuses}
              onChange={(statuses) => set({ statuses })}
              options={statusOptions}
            />
          )}
        </div>
      </div>

      {tab === 'favorites' && counts.favorites === 0 && (
        <p className="mt-3 text-xs text-muted dark:text-muted-dark">{t('reports.utilization.favorites.explainer')}</p>
      )}
    </div>
  );
};

export default UtilizationControls;
